// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 0356bd21-7ce3-4e39-8634-00a7b3b689ef

import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Stack, TextField, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InventoryIcon from '@mui/icons-material/Inventory2';
import { TypeBadge } from './ui';
import store from './store';

// Optimum-only master catalog: canonical app definitions + cross-client
// adoption. Build once, reuse per engagement. Clients never see this.
export default function MasterCatalog({ onOpenInstance }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');

  useEffect(() => { store.catalogAdoption().then(setRows); }, []);

  const filtered = rows.filter(
    (r) => r.name.toLowerCase().includes(q.toLowerCase()) || (r.vendor || '').toLowerCase().includes(q.toLowerCase())
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <InventoryIcon sx={{ color: 'sectionHeader.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Master Application Catalog</Typography>
        <Chip size="small" label="Optimum-owned · reused across clients" sx={{ bgcolor: '#eef3ff', color: '#0B5FFF' }} />
        <Box sx={{ flex: 1 }} />
        <TextField size="small" placeholder="Search catalog…" value={q} onChange={(e) => setQ(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
      </Stack>
      <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
        Canonical definitions built once. When a client has one of these apps, the discovery team adds it from the
        catalog and only fills in client-specific facts — no reinventing the wheel. Adoption below is visible to
        Optimum only; clients cannot see each other.
      </Typography>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
              <TableCell>Application</TableCell>
              <TableCell>Vendor</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Typical Hosting</TableCell>
              <TableCell>Default TIME / 6R</TableCell>
              <TableCell>Versions in use</TableCell>
              <TableCell>Used by</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TypeBadge type="Application" size={18} />
                    <b>{r.name}</b>
                  </Stack>
                  <Typography variant="caption" sx={{ color: '#888' }}>{r.description}</Typography>
                </TableCell>
                <TableCell>{r.vendor}</TableCell>
                <TableCell>{r.category}</TableCell>
                <TableCell>{r.typicalHosting}</TableCell>
                <TableCell>
                  <Chip size="small" label={r.defaultTime} sx={{ mr: 0.5 }} />
                  <Chip size="small" variant="outlined" label={r.default6R} />
                </TableCell>
                <TableCell>
                  {r.versions && r.versions.length ? (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {r.versions.map((v) => (
                        <Chip key={v} size="small" label={v} sx={{ bgcolor: '#fff3e0', color: '#8a5a00' }} />
                      ))}
                    </Stack>
                  ) : (
                    <span style={{ color: '#bbb' }}>—</span>
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <Chip size="small" color={r.clientCount > 1 ? 'primary' : 'default'}
                      label={`${r.clientCount} client${r.clientCount === 1 ? '' : 's'}`} />
                    {r.clients.map((c) => (
                      <Chip key={c.id} size="small" variant="outlined"
                        label={c.version ? `${c.name} · ${c.version}` : c.name}
                        onClick={() => c.instanceId && onOpenInstance(c.instanceId)}
                        sx={{ cursor: 'pointer' }} />
                    ))}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
